<template>
    <v-modal @opened="fetchData">
        <slot slot="title">{{ $t('preorder.targets') }}</slot>
        <slot slot="buttonContent">
            <v-block type="icon-block" height="stretch" color="white">
                <svgicon class="u-opacity-25" name="graphs" width="40" height="36"></svgicon>
                <p>{{ $t('preorder.targets') }}</p>
            </v-block>
        </slot>
        <div class="c-targets o-gutter">
            <v-loading :data="PreOrderTargetsAndTotals"></v-loading>
            <v-blocks>
                <v-block>
                    <div class="c-icon-label">
                        <svgicon name="box" width="25px" height="25px" class="u-svg-color-alpha-base"></svgicon>
                        <div class="u-fullwidth">
                            <strong class="u-font-default">{{ $t('preorder_targets_and_totals.totals') }}</strong>
                            <div class="o-grid o-grid--half">
                                <div>
                                    <table class="">
                                        <tr>
                                            <td>{{ $t('preorder_targets_and_totals.product_count') }}:</td>
                                            <td>{{ PreOrderTargetsAndTotals.ProductCount }}</td>
                                        </tr>
                                        <tr>
                                            <td>{{ $t('preorder_targets_and_totals.product_variant_amount') }}:</td>
                                            <td>{{ PreOrderTargetsAndTotals.ProductVariantAmount }}</td>
                                        </tr>
                                        <tr>
                                            <td>{{ $t('preorder_targets_and_totals.total_excl_VAT') }}:</td>
                                            <td>{{ PreOrderTargetsAndTotals.TotalProductPriceAfterDiscount | currency(customerCurrencySymbol) }}</td>
                                        </tr>
                                    </table>
                                </div>
                                <div>
                                    <table class="">
                                        <tr>
                                            <td>&nbsp;</td>
                                        </tr>
                                        <tr>
                                            <td>{{ $t('preorder_targets_and_totals.marketing_product_variant_amount') }}:</td>
                                            <td>{{ PreOrderTargetsAndTotals.MarketingProductVariantAmount }}</td>
                                        </tr>
                                        <tr>
                                            <td>{{ $t('preorder_targets_and_totals.marketing_total_excl_VAT') }}:</td>
                                            <td>{{ PreOrderTargetsAndTotals.TotalMarketingPriceAfterDiscount | currency(customerCurrencySymbol) }}</td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-block>
                <v-block>
                    <div class="c-icon-label">
                        <target-icon></target-icon>
                        <div class="u-fullwidth">
                            <strong class="u-font-default">{{ $t('preorder.targets') }}</strong>
                            <div class="o-grid o-grid--half">
                                <div>
                                    <table class="">
                                        <tr>
                                            <td>{{ PreOrderTargetsAndTotals.TargetPrice | currency(customerCurrencySymbol) }}</td>
                                            <td>
                                                <template v-if="PreOrderTargetsAndTotals.TargetGrowthPercentage>=0">
                                                    <strong class="u-color-success-base">(+{{ PreOrderTargetsAndTotals.TargetGrowthPercentage }}%)</strong>
                                                </template>
                                                <template v-else>
                                                    <strong class="u-color-error-base">({{ PreOrderTargetsAndTotals.TargetGrowthPercentage }}%)</strong>
                                                </template>
                                            </td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-block>
            </v-blocks>
            <v-block v-if="PreOrderTargetsAndTotals.Comparisons && PreOrderTargetsAndTotals.Comparisons.length" color="neutral">
                <strong class="u-block u-font-default u-margin-bottom-tiny">{{ $t('global.compare') }}</strong>
                <div class="o-grid o-grid--third o-gutter u-flex--align-items-center o-page-row--small">
                    <v-custom-select v-model="SelectedPreOrderSheet"
                                     :options="PreOrderTargetsAndTotals.Comparisons"
                                     option-name="PreOrderSheetName"
                                     option-value="PreOrderSheetName"
                                     search-label="PreOrderSheetName"
                                     :horizontal="false"
                                     @change="updateSelectedPreOrderSheet"></v-custom-select>
                    <div class="u-flex-set u-flex--content-center">
                        <difference-icon></difference-icon>
                    </div>
                    <table class="u-margin-none">
                        <tr>
                            <td>
                                <strong>{{ PreOrderTargetsAndTotals.PreOrderSheetName }}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {{ PreOrderTargetsAndTotals.TotalProductPriceAfterDiscount | currency(customerCurrencySymbol) }} ({{ PreOrderTargetsAndTotals.ProductVariantAmount }} stuks)
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <template v-if="SelectedPreOrderSheet.GrowthPercentage>=0">
                                    <strong class="u-color-success-base">(+{{ SelectedPreOrderSheet.GrowthPercentage }}%)</strong>
                                </template>
                                <template v-else>
                                    <strong class="u-color-error-base">({{ SelectedPreOrderSheet.GrowthPercentage }}%)</strong>
                                </template>
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="o-page-row--small">{{ SelectedPreOrderSheet.TotalPrice | currency(customerCurrencySymbol) }} ({{ SelectedPreOrderSheet.ProductVariantAmount }} stuks)</div>
                <table>
                    <tr>
                        <td>{{ $t('preorder_targets_and_totals.twenty_percent_growth_amount') }}:</td>
                        <td>
                            <template v-if="SelectedPreOrderSheet.TwentyPercentGrowthAmount==0">
                                <strong class="u-color-success-base">€ {{ SelectedPreOrderSheet.TwentyPercentGrowthAmount }}</strong>
                            </template>
                            <template v-else>
                                <strong class="u-color-alpha-base">€ {{ SelectedPreOrderSheet.TwentyPercentGrowthAmount }}</strong>
                            </template>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('preorder_targets_and_totals.fifty_percent_growth_amount') }}:</td>
                        <td>
                            <template v-if="SelectedPreOrderSheet.FiftyPercentGrowthAmount==0">
                                <strong class="u-color-success-base">€ {{ SelectedPreOrderSheet.FiftyPercentGrowthAmount }}</strong>
                            </template>
                            <template v-else>
                                <strong class="u-color-alpha-base">€ {{ SelectedPreOrderSheet.FiftyPercentGrowthAmount }}</strong>
                            </template>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('preorder_targets_and_totals.hundred_percent_growth_amount') }}:</td>
                        <td>
                            <template v-if="SelectedPreOrderSheet.HundredPercentGrowthAmount === 0">
                                <strong class="u-color-success-base">€ {{ SelectedPreOrderSheet.HundredPercentGrowthAmount }}</strong>
                            </template>
                            <template v-else>
                                <strong class="u-color-alpha-base">€ {{ SelectedPreOrderSheet.HundredPercentGrowthAmount }}</strong>
                            </template>
                        </td>
                    </tr>
                </table>
            </v-block>

        </div>
    </v-modal>
</template>

<script>
    import VModal from '@/components/modal/Modal'
    import VBlocks from '@/components/blocks/Blocks'
    import targetIcon from '@/assets/images/svg/targets.svg'
    import differenceIcon from '@/assets/images/svg/difference.svg'
    import VCustomSelect from '@/components/form/CustomSelect'
    import VLoading from '@/components/Loading'

    // Store
    import {mapGetters} from 'vuex'

    export default {
        components: {
            VModal,
            VBlocks,
            targetIcon,
            differenceIcon,
            VCustomSelect,
            VLoading
        },
        data() {
            return {
                SelectedPreOrderSheet: {}
            }
        },
        props: {},
        computed: {
            ...mapGetters({
                PreOrderTargetsAndTotals: 'PreOrderTargetsAndTotals'
            }),
            customerCurrencySymbol() { 
                return this.$store.getters.userInfo.customerCurrencySymbol;
            }
        },
        methods: {
            fetchData () {
                this.$store.dispatch('getPreOrderTargetsAndTotals', this.$route.params.id).then((response) => {
                    this.SelectedPreOrderSheet = this.PreOrderTargetsAndTotals.Comparisons.find((obj) => { return obj.IsDefault === true; });
                });
            },
            updateSelectedPreOrderSheet(value){
                this.SelectedPreOrderSheet = value;
            }
        }
    }
</script>

<style lang="scss" scoped>

    @import "~@/assets/scss/private";

</style>


// WEBPACK FOOTER //
// src/components/modal/common/TargetsAndTotals.vue